import ChipPressableComponent from "../../../Chips/ComponentChipPressable/Index";
import { useEditOnboarding } from "../../../../hooks/useEditOnboarding";
import { useTaskCreation } from "../../../../hooks/useTaskCreation";
import TableTaskEvaluation from "../../../Table/TaskEvaluation";
import TableEvaluation from "../../../Table/PastureEvaluator";
import { SearchInputV2 } from "../../../FormControlsV2";
import { useFilter } from "../../../../hooks/useFilter";
import { DateModal } from "../../../Modal/DateFilter";
import TableProducts from "../../../Table/Products";
import { useEffect, useState, useRef } from "react";
import axios from "../../../../services/axios";
import { format } from "date-fns";
import FailSyncTable from "../../../Table/FailSync/Index";
import { useTableMonitor } from "../../../../hooks/useTableMonitor";
import TablePastureManagement from "../../../Table/PastureManagement";
import NotificationTable from "../../../Table/Notifications/Index";
import DownloadChip from "../../../Chips/ComponentChipDownload/Index";
import { ButtonOnlyTitle } from "../../../Buttons/ButtonOnlyTitle/Index";
import { exportTasksMonitoringToExcel } from "../../../../utils/excel/exportTasksMonitoring";
import "./styles.css";
import { useTranslation } from "react-i18next";
import { translateMonitoringTask } from "../../../../i18n/translateMonitoringTask";
import { TableFilterRow, TableViewport } from "../../TableLayout";

export type IOptionsEvaluation =
  | "HEIGHT"
  | "DAYS"
  | "RATING"
  | "STRIP"
  | "FINISHED"
  | "COMPLETED"
  | "PENDING"
  | "ERROR"
  | "NOTIFICATIONS"
  | "ARCHIVE"
  | "CANCELED"
  | "ACCEPTED";

export function ViewTables() {
  const { t, i18n } = useTranslation();
  const { queryToSearch, setQueryToSearch, setFilterSelected, setOptionForm } =
    useEditOnboarding();
  const { modalStartEvaluator, setModalStartEvaluator, setStartedEvaluation } =
    useTaskCreation();
  const { selectedOrder, sortAsc } = useTableMonitor();

  const [searchable, setSearchable] = useState(true);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [listTasks, setListTasks] = useState<ITaskEvaluation[]>([]);
  const [listEvaluations, setListEvaluations] = useState<
    IMicroAreaEvaluation[]
  >([]);
  const [listProducts, setListProducts] = useState<IProducts[]>([]);
  const [listFailsSync, setListFailSync] = useState<IFailSync[]>([]);
  const [listNotifications, setListNotifications] = useState<
    ITaskNotification[]
  >([]);
  const [listPastureManagement, setListPastureManagement] =
    useState<IMicroAreaManagement>();
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<IOptionsEvaluation | undefined>(
    undefined,
  );
  const {
    sideBarOptionSelected,
    systemManager,
    rangeDays,
    reloadTasks,
    pastureManagementHistory,
    setPastureManagementHistory,
  } = useFilter();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [haveEvaluator, setHaveEvaluator] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false);

  const quantity = new Map<string, number>();
  quantity.set("PRODUCTS", 8);
  quantity.set("PASTURE_EVALUATION", 7);
  quantity.set("TASK_EVALUATION", 7);
  quantity.set("FAIL_SYNC", 7);
  quantity.set("PASTURE_MANAGEMENT", 7);

  const [optionSelected, setOptionSelected] = useState<null | string>(null);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1024px)");
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    setIsMobile(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (sideBarOptionSelected == "MANAGER_SYSTEM") {
      setOptionSelected(systemManager);
    } else {
      setOptionSelected(sideBarOptionSelected);
    }
  }, [systemManager, sideBarOptionSelected]);

  useEffect(() => {
    if (systemManager !== "TASK_EVALUATION") {
      setSearchSuggestions([]);
      return;
    }

    let cancelled = false;
    axios.get("/tasks/taskType").then(({ data: taskTypes }) => {
      const TaskTypeEnumObj = Object.keys(taskTypes).reduce(
        (acc, key) => {
          acc[key] = taskTypes[key];
          return acc;
        },
        {} as Record<string, string>,
      );

      if (typeof window !== "undefined" && window.localStorage) {
        localStorage.setItem(
          "TaskTypeEnumObj",
          JSON.stringify(TaskTypeEnumObj),
        );
      }

      return axios.get("/tasks/summary").then(({ data: taskSummaries }) => {
        if (cancelled) return;
        const updated = taskSummaries.map((task: any) => ({
          ...task,
          type: TaskTypeEnumObj[task.task_type],
        }));
        const types = Array.from(
          new Set(updated.map((t: any) => translateMonitoringTask(t.type))),
        ).filter(Boolean) as string[];
        const userNames = Array.from(
          new Set(updated.map((t: any) => t.user_name)),
        ).filter(Boolean) as string[];
        const initials = Array.from(
          new Set(
            updated.map((t: any) =>
              t.initials != ""
                ? `${t.initials.split("-")[0]}-${t.initials.split("-")[1]}`
                : undefined,
            ),
          ),
        ).filter(Boolean) as string[];
        setSearchSuggestions([...types, ...userNames, ...initials]);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [systemManager, i18n.language]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    let didCancel = false;

    setIsLoading(true);
    isFetchingRef.current = true;

    const page = currentPage - 1;
    const size = 16;
    const dateBegin = rangeDays?.from
      ? format(new Date(rangeDays.from), "y-MM-dd")
      : undefined;
    const dateEnd = rangeDays?.to
      ? format(new Date(rangeDays.to), "y-MM-dd")
      : undefined;

    if (optionSelected === "PRODUCTS") {
      setSearchable(false);
      axios
        .get(`/products`, {
          params: {
            size,
            page,
          },
          // signal,
        })
        .then(({ data }) => {
          if (currentPage === 1) setListProducts(data.content);
          else setListProducts((prev) => [...prev, ...data.content]);
          setTotalPages(data.total_pages);
        })
        .finally(() => {
          if (!didCancel) {
            setIsLoading(false);
            isFetchingRef.current = false;
          }
        });
    } else if (optionSelected === "PASTURE_EVALUATION") {
      setFilterSelected("evaluations");
      setSearchable(true);
      axios
        .get(`/tasks/pasture-evaluation`, {
          params: {
            ...(filters ? { status: filters } : {}),
            size,
            page,
            ...(queryToSearch ? { query: queryToSearch } : {}),
            ...(dateBegin ? { date_begin: dateBegin } : {}),
            ...(dateEnd ? { date_end: dateEnd } : {}),
            sort_by: selectedOrder,
            sort_direction: sortAsc ? "asc" : "desc",
          },
          // signal,
        })
        .then(({ data }) => {
          setStartedEvaluation(data.total_elements > 0);
          if (currentPage === 1) setListEvaluations(data.content);
          else setListEvaluations((prev) => [...prev, ...data.content]);
          setTotalPages(data.total_pages);
        })
        .finally(() => {
          if (!didCancel) {
            setIsLoading(false);
            isFetchingRef.current = false;
          }
        });
    } else if (optionSelected === "PASTURE_MANAGEMENT") {
      setSearchable(true);
      const endpoint = pastureManagementHistory
        ? "/micro-area-management/history"
        : "/micro-area-management";
      axios
        .get(endpoint, {
          params: {
            size,
            page,
            micro_area_monitoring_type: filters,
            query: queryToSearch || "",
          },
          // signal,
        })
        .then((response) => {
          let payload: IMicroAreaManagement;

          if (pastureManagementHistory) {
            const history = response.data as IMicroAreaManagementHistory;

            const converted: IMicroAreaManagement = {
              ...history,
              content: history.content.map((entry) => {
                return {
                  id: entry.id,
                  type: {
                    ...entry.management_type,
                    monitoring_types: [
                      {
                        id: entry.monitoring_type.id,
                        name: entry.monitoring_type.name,
                      },
                    ],
                  },
                  created_at: entry.start_date,
                  micro_area: Array.isArray(entry.micro_area)
                    ? entry.micro_area
                    : ([entry.micro_area] as any),
                  requirement_in: entry.requirement_in,
                  requirement_out: entry.requirement_out ?? 0,
                  frequency: entry.frequency,
                  days_of_week: entry.days_of_week,
                  execution_per_day: entry.execution_per_day,
                  start_date: entry.start_date,
                  deactivated_at:
                    entry.micro_area_management_group.deactivated_at,
                  micro_area_management_group_initials:
                    entry.micro_area_management_group_initials,
                } as IMicroAreaManagementContent;
              }),
            };

            payload = converted;
          } else {
            payload = response.data as IMicroAreaManagement;
          }

          if (currentPage === 1) {
            setListPastureManagement(payload);
          } else {
            setListPastureManagement(
              (prev) =>
                ({
                  ...payload,
                  content: [
                    ...(prev?.content || []),
                    ...(payload.content || []),
                  ],
                }) as any,
            );
          }
          setTotalPages(payload.total_pages);
        })
        .finally(() => {
          if (!didCancel) {
            setIsLoading(false);
            isFetchingRef.current = false;
          }
        });
    } else if (optionSelected === "TASK_EVALUATION") {
      setFilterSelected("task-evaluations");
      setSearchable(true);

      if (filters === "NOTIFICATIONS") {
        setSearchable(false);
        axios
          .get(`/tasks/notification`, {
            params: {
              size,
              page,
              ...(dateBegin ? { date_begin: dateBegin } : {}),
              ...(dateEnd ? { date_end: dateEnd } : {}),
              sort_by: selectedOrder,
              sort_direction: sortAsc ? "asc" : "desc",
            },
            signal,
          })
          .then(({ data }) => {
            const content = data.content || [];

            if (currentPage === 1) setListNotifications(content);
            else setListNotifications((prev) => [...prev, ...content]);
            setTotalPages(data.totalPages ?? data.total_pages);
          })
          .finally(() => {
            if (!didCancel) {
              setIsLoading(false);
              isFetchingRef.current = false;
            }
          });
      } else if (filters !== "ERROR") {
        let extraParams: Record<string, any> = {};
        try {
          const taskTypeEnumObj = JSON.parse(
            localStorage.getItem("TaskTypeEnumObj") || "{}",
          );
          if (queryToSearch && typeof queryToSearch === "string") {
            const matchedKey = Object.keys(taskTypeEnumObj).find(
              (key) => translateMonitoringTask(taskTypeEnumObj[key]) === queryToSearch,
            );
            if (matchedKey) {
              extraParams.task_type = matchedKey;
            } else {
              extraParams.query = queryToSearch;
            }
          }
        } catch (e) {
          if (queryToSearch) extraParams.query = queryToSearch;
        }

        axios
          .get(`/tasks/monitor`, {
            params: {
              ...(filters ? { status: filters } : {}),
              size,
              page,
              ...extraParams,
              ...(dateBegin ? { date_begin: dateBegin } : {}),
              ...(dateEnd ? { date_end: dateEnd } : {}),
              sort_by: selectedOrder,
              sort_direction: sortAsc ? "asc" : "desc",
            },
            signal,
          })
          .then(({ data }) => {
            if (currentPage === 1) setListTasks(data.content);
            else setListTasks((prev) => [...prev, ...data.content]);
            setTotalPages(data.total_pages);
          })
          .finally(() => {
            if (!didCancel) {
              setIsLoading(false);
              isFetchingRef.current = false;
            }
          });
      } else {
        setIsLoading(true);
        setSearchable(false);
        axios
          .get(`/sync/failures`, {
            params: {
              size,
              page,
              sort_by: selectedOrder,
              sort_direction: sortAsc ? "asc" : "desc",
            },
            signal,
          })
          .then(({ data }) => {
            const mapped = data.content.map((el: any) => ({
              id: el.id,
              date: new Date(el.created_at),
              name: el.name,
            }));
            if (currentPage === 1) setListFailSync(mapped);
            else setListFailSync((prev) => [...prev, ...mapped]);
            setTotalPages(data.total_pages);
          })
          .finally(() => {
            if (!didCancel) {
              setIsLoading(false);
              isFetchingRef.current = false;
            }
          });
      }
    }

    return () => {
      didCancel = true;
      controller.abort();
    };
  }, [
    modalStartEvaluator,
    optionSelected,
    currentPage,
    queryToSearch,
    filters,
    rangeDays,
    reloadTasks,
    selectedOrder,
    sortAsc,
    pastureManagementHistory,
    setStartedEvaluation,
  ]);

  useEffect(() => {
    setListProducts([]);
    setListEvaluations([]);
    setListPastureManagement(undefined);
    setListTasks([]);
    setListFailSync([]);
    setListNotifications([]);
  }, [filters, selectedOrder, sortAsc]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, rangeDays, queryToSearch, pastureManagementHistory]);

  useEffect(() => {
    setCurrentPage(1);
  }, [modalStartEvaluator]);

  useEffect(() => {
    axios.get("users/user/evaluator").then(({ data }) => {
      setHaveEvaluator(
        data.filter((el: any) => el.status == "ACTIVE").length > 0,
      );
    });
  }, [modalStartEvaluator]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - 150) {
        if (!isLoading && !isFetchingRef.current && currentPage < totalPages) {
          isFetchingRef.current = true;
          setCurrentPage((prev) => prev + 1);
        }
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [isLoading, currentPage, totalPages]);

  const handleExcelExport = async () => {
    await exportTasksMonitoringToExcel({
      rangeDays,
      queryToSearch,
      filters,
      selectedOrder,
      sortAsc,
    });
  };

  useEffect(() => {
    setListPastureManagement(undefined);
  }, [pastureManagementHistory]);

  const isSearchLoading = isLoading && currentPage === 1;
  const isPaginationLoading = isLoading && currentPage > 1;

  return (
    <>
      {/* Filtro */}
      <TableFilterRow hidden={optionSelected == "PRODUCTS"}>
        {optionSelected == "PASTURE_EVALUATION" && (
          <>
            <ChipPressableComponent
              theme={filters == undefined ? "menu-dark" : "menu-light"}
              label={t("systemManager.all")}
              onClick={() => setFilters(undefined)}
            />
            <ChipPressableComponent
              theme={filters == "COMPLETED" ? "menu-dark" : "menu-light"}
              label={t("systemManager.evaluated")}
              onClick={() => setFilters("COMPLETED")}
            />
            <ChipPressableComponent
              theme={filters == "ARCHIVE" ? "menu-dark" : "menu-light"}
              label={t("systemManager.archived")}
              onClick={() => setFilters("ARCHIVE")}
            />
            <ChipPressableComponent
              theme={filters == "CANCELED" ? "menu-dark" : "menu-light"}
              label={t("systemManager.canceled")}
              onClick={() => setFilters("CANCELED")}
            />
            <ChipPressableComponent
              theme={filters == "PENDING" ? "menu-dark" : "menu-light"}
              label={t("systemManager.pending")}
              onClick={() => setFilters("PENDING")}
            />
          </>
        )}
        {optionSelected == "TASK_EVALUATION" && (
          <>
            <ChipPressableComponent
              theme={filters == undefined ? "menu-dark" : "menu-light"}
              label={t("systemManager.all")}
              onClick={() => setFilters(undefined)}
            />
            <ChipPressableComponent
              theme={filters == "COMPLETED" ? "menu-dark" : "menu-light"}
              label={t("systemManager.completed")}
              onClick={() => setFilters("COMPLETED")}
            />
            <ChipPressableComponent
              theme={filters == "PENDING" ? "menu-dark" : "menu-light"}
              label={t("systemManager.pending")}
              onClick={() => setFilters("PENDING")}
            />
            <ChipPressableComponent
              theme={filters == "ERROR" ? "menu-dark" : "menu-light"}
              label={t("systemManager.syncFailure")}
              onClick={() => setFilters("ERROR")}
            />
            <ChipPressableComponent
              theme={filters == "NOTIFICATIONS" ? "menu-dark" : "menu-light"}
              label={t("systemManager.notifications")}
              onClick={() => setFilters("NOTIFICATIONS")}
            />
          </>
        )}
        {optionSelected == "PASTURE_MANAGEMENT" && (
          <>
            <ChipPressableComponent
              theme={filters == undefined ? "menu-dark" : "menu-light"}
              label={t("systemManager.all")}
              onClick={() => setFilters(undefined)}
            />
            <ChipPressableComponent
              theme={filters == "HEIGHT" ? "menu-dark" : "menu-light"}
              label={t("systemManager.height")}
              onClick={() => setFilters("HEIGHT")}
            />
            <ChipPressableComponent
              theme={filters == "DAYS" ? "menu-dark" : "menu-light"}
              label={t("systemManager.days")}
              onClick={() => setFilters("DAYS")}
            />
            <ChipPressableComponent
              theme={filters == "RATING" ? "menu-dark" : "menu-light"}
              label={t("systemManager.rating")}
              onClick={() => setFilters("RATING")}
            />
            <ChipPressableComponent
              theme={filters == "STRIP" ? "menu-dark" : "menu-light"}
              label={t("systemManager.strip")}
              onClick={() => setFilters("STRIP")}
            />
          </>
        )}

        {searchable && (
          <>
            <SearchInputV2
              name="monitoramentos-search"
              suggestions={searchSuggestions}
              onSearch={(query) => setQueryToSearch(query)}
              onChange={(value) => {
                if (value === "") setQueryToSearch(undefined);
              }}
            />
          </>
        )}

        {optionSelected == "PASTURE_EVALUATION" && (
          <DownloadChip
            enableReport
            onReportSelect={() => setOptionForm("evaluation-report")}
          />
        )}

        {optionSelected == "TASK_EVALUATION" &&
          filters != "ERROR" &&
          filters != "NOTIFICATIONS" && (
            <DownloadChip enableExcel onExcelSelect={handleExcelExport} />
          )}

        {optionSelected == "PASTURE_EVALUATION" && (
          <div className="filter__content-action" style={{ minWidth: 200 }}>
            <ButtonOnlyTitle
              title={t("systemManager.startEvaluation")}
              theme={"info"}
              type="button"
              onClick={() => setModalStartEvaluator(true)}
              disabled={!haveEvaluator}
            />
          </div>
        )}

        {optionSelected == "PASTURE_MANAGEMENT" && isMobile && (
          <div className="filters_pasture_management">
            <ButtonOnlyTitle
              title={t("systemManager.active")}
              theme={pastureManagementHistory ? "light" : "dark"}
              type="button"
              style={{ width: 200 }}
              onClick={() => {
                if (pastureManagementHistory)
                  setPastureManagementHistory(false);
              }}
            />
            <ButtonOnlyTitle
              title={t("systemManager.inactive")}
              theme={pastureManagementHistory ? "dark" : "light"}
              type="button"
              style={{ width: 200 }}
              onClick={() => {
                if (!pastureManagementHistory)
                  setPastureManagementHistory(true);
              }}
            />
          </div>
        )}
      </TableFilterRow>
      {/* Filtro */}

      {/* Visualização dos avaliadores */}

      <TableViewport
        loading={isSearchLoading}
        containerRef={containerRef}
        containerStyle={{ borderRadius: 10, paddingRight: 5 }}
      >
          {optionSelected == "TASK_EVALUATION" &&
          filters != "ERROR" &&
          filters != "NOTIFICATIONS" ? (
            <TableTaskEvaluation
              listTable={listTasks}
              filter={filters}
              isLoading={isPaginationLoading}
            />
          ) : (
            <></>
          )}
          {optionSelected == "TASK_EVALUATION" && filters == "ERROR" ? (
            <FailSyncTable
              listTable={listFailsSync}
              isLoading={isPaginationLoading}
            />
          ) : (
            <></>
          )}
          {optionSelected == "TASK_EVALUATION" && filters == "NOTIFICATIONS" ? (
            <NotificationTable
              listTable={listNotifications}
              isLoading={isPaginationLoading}
            />
          ) : (
            <></>
          )}
          {optionSelected == "PASTURE_EVALUATION" ? (
            <TableEvaluation
              listTable={listEvaluations}
              filter={filters}
              isLoading={isPaginationLoading}
            />
          ) : (
            <></>
          )}
          {optionSelected == "PRODUCTS" ? (
            <TableProducts
              listTable={listProducts}
              isLoading={isPaginationLoading}
            />
          ) : (
            <></>
          )}
          {optionSelected == "PASTURE_MANAGEMENT" ? (
            <TablePastureManagement
              isHistory={pastureManagementHistory}
              listTable={
                listPastureManagement ? listPastureManagement?.content : []
              }
              isLoading={isPaginationLoading}
            />
          ) : (
            <></>
          )}
      </TableViewport>
      {/* Visualização dos avaliadores */}

      {/*Modals*/}
      <DateModal />
      {/*Modals*/}
    </>
  );
}
